import React, { useState, useRef, useEffect, useCallback } from 'react';

const CATEGORIAS = [
  { value: 'averia', label: '🔧 Avería', color: '#f59e0b' },
  { value: 'ruido', label: '🔊 Ruido', color: '#ef4444' },
  { value: 'dano', label: '💥 Daño a propiedad', color: '#dc2626' },
  { value: 'estacionamiento', label: '🅿️ Estacionamiento', color: '#6366f1' },
  { value: 'mascota', label: '🐕 Mascota', color: '#8b5cf6' },
  { value: 'basura', label: '🗑️ Basura', color: '#06b6d4' },
  { value: 'seguridad', label: '🔐 Seguridad', color: '#ef4444' },
  { value: 'otro', label: '📌 Otro', color: '#64748b' },
];

export function HashtagAutocomplete({ value = [], onChange, residentes = [], placeholder = 'Escribe # para etiquetar un apartamento...' }) {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Parse hashtags from texto
  const parseText = useCallback((text) => {
    const tags = [];
    const regex = /#(\w+)/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      tags.push(match[1]);
    }
    return tags;
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setInput(val);
    const pos = e.target.selectionStart;
    setCursorPos(pos);

    // Detectar si está escribiendo un hashtag
    const beforeCursor = val.substring(0, pos);
    const hashIndex = beforeCursor.lastIndexOf('#');
    if (hashIndex !== -1) {
      const afterHash = beforeCursor.substring(hashIndex + 1);
      const afterCursor = val.substring(pos);
      // No hay espacio después del #
      if (!afterHash.includes(' ') && !afterHash.includes('\n')) {
        const search = afterHash.toLowerCase();
        const filtered = residentes.filter(r =>
          r.apartamento?.toLowerCase().includes(search) ||
          r.nombre_completo?.toLowerCase().includes(search) ||
          r.piso?.toString().includes(search)
        ).slice(0, 6);
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
        return;
      }
    }
    setShowSuggestions(false);

    // Actualizar hashtags
    const tags = parseText(val);
    onChange([...new Set(tags)]);
  };

  const selectSuggestion = (residente) => {
    const val = input;
    const pos = cursorPos;
    const beforeCursor = val.substring(0, pos);
    const hashIndex = beforeCursor.lastIndexOf('#');
    const before = val.substring(0, hashIndex);
    const after = val.substring(pos);
    const tag = residente.apartamento?.replace(/\s+/g, '') || 'apto';
    const newVal = `${before}#${tag} ${after}`;
    setInput(newVal);
    setShowSuggestions(false);
    const tags = parseText(newVal);
    onChange([...new Set(tags)]);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <textarea
        ref={inputRef}
        value={input}
        onChange={handleInput}
        placeholder={placeholder}
        rows={4}
        style={{
          width: '100%', borderRadius: 'var(--radius-sm)', padding: 12,
          border: '1px solid var(--border)', fontFamily: 'inherit',
          fontSize: '0.9rem', resize: 'vertical', lineHeight: 1.5
        }}
      />
      {showSuggestions && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, right: 0,
          background: '#fff', borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          border: '1px solid var(--border)', zIndex: 50, maxHeight: 200,
          overflow: 'auto', marginBottom: 4
        }}>
          {suggestions.map((r) => (
            <div key={r.id}
              onClick={() => selectSuggestion(r)}
              style={{
                padding: '10px 14px', cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: 8, borderBottom: '1px solid #f1f5f9',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(99,102,241,0.06)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary)' }}>
                #{r.apartamento}
              </span>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {r.nombre_completo} · P{r.piso}
              </span>
            </div>
          ))}
        </div>
      )}
      {value.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {value.map((tag) => (
            <span key={tag} style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))',
              color: 'var(--primary)', padding: '3px 12px', borderRadius: 20,
              fontSize: '0.78rem', fontWeight: 600, border: '1px solid rgba(99,102,241,0.2)'
            }}>
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export { CATEGORIAS };